import { MessageReaction, TimelineRow } from "@/services/conversations.api";

/** Lo que manda el backend por `message:reaction`. */
export interface ReactionUpdate {
  conversationId: string;
  messageId?: string;
  /** Segunda llave: una burbuja que entró en vivo puede no tener todavía el id interno. */
  providerMessageId?: string;
  reactions: MessageReaction[];
  /**
   * Burbuja a sacar del chat.
   *
   * Una reacción que llegó **ilegible** se guardó como un mensaje más, con el placeholder
   * `[mensaje en camino…]`: en ese momento nadie sabía que era una reacción. Al reenviarla
   * descifrable resulta que ese mensaje no existió nunca, y si no se saca queda clavado en el chat
   * con la reacción mostrándose al lado.
   *
   * Viene en el mismo evento a propósito: quitar el fantasma y mostrar la reacción son la misma
   * cosa vista desde el chat, y separarlos dejaría un parpadeo entre las dos actualizaciones.
   */
  placeholderMessageId?: string;
}

/**
 * Aplica un `message:reaction` sobre el timeline.
 *
 * **Una reacción no agrega una fila**: parchea la burbuja que ya está en pantalla, igual que
 * `message:repaired`. Si se insertara como fila, cada 👍 sería una burbuja ordenada
 * cronológicamente empujando la conversación hacia abajo.
 *
 * Busca por **las dos llaves** por lo mismo que la reparación: un mensaje que entró en vivo puede
 * no tener el id interno todavía. Es la lección de conversation-service!39 — buscar sólo por
 * `messageId` dejaba el `[mensaje en camino…]` en pantalla hasta el próximo refetch.
 *
 * Se reemplaza el set completo y no se hace merge: el backend manda todas las reacciones del
 * mensaje, así que el último evento gana y no hay forma de divergir.
 */
export function aplicarReaccion(filas: TimelineRow[], u: ReactionUpdate): TimelineRow[] {
  if (!u.messageId && !u.providerMessageId && !u.placeholderMessageId) return filas;
  // El fantasma se saca ANTES de parchear: si quedara, el chat mostraría la reacción y arriba una
  // burbuja de un mensaje que nunca existió.
  const base = u.placeholderMessageId
    ? filas.filter(
        (r) => !(r.kind === "message" && r.payload.messageId === u.placeholderMessageId),
      )
    : filas;
  let tocada = base.length !== filas.length;
  const nuevas = base.map((r) => {
    if (r.kind !== "message") return r;
    const esLaFila =
      (!!u.messageId && r.payload.messageId === u.messageId) ||
      (!!u.providerMessageId && r.payload.providerMessageId === u.providerMessageId);
    if (!esLaFila) return r;
    tocada = true;
    return {
      ...r,
      payload: {
        ...r.payload,
        // Adopta el id interno si no lo tenía, igual que en la reparación: a partir de acá se la
        // encuentra por cualquiera de las dos llaves.
        messageId: u.messageId ?? r.payload.messageId,
        reactions: u.reactions,
      },
    };
  });
  // Sin fila que tocar no se devuelve un array nuevo: evita un re-render de todo el chat por una
  // reacción a un mensaje que no está en la ventana cargada.
  return tocada ? nuevas : filas;
}

/**
 * Agrupa las reacciones por emoji, para mostrar un chip por emoji con quiénes lo pusieron.
 *
 * En un chat 1:1 hay a lo sumo un contacto y unos pocos operadores, así que **no se agrupa por
 * cantidad** ("3 👍"): se muestran todos y el tooltip dice quién.
 */
export function agruparPorEmoji(
  reacciones: MessageReaction[] | undefined,
): { emoji: string; quienes: string[] }[] {
  const mapa = new Map<string, string[]>();
  for (const r of reacciones ?? []) {
    const lista = mapa.get(r.emoji) ?? [];
    lista.push(r.reactorName || (r.reactorType === "contact" ? "Contacto" : "Operador"));
    mapa.set(r.emoji, lista);
  }
  return [...mapa.entries()].map(([emoji, quienes]) => ({ emoji, quienes }));
}

/**
 * Los seis de WhatsApp. Es el set que la gente espera: cualquier otro sería una elección nuestra
 * que el contacto no reconoce.
 */
export const EMOJIS_REACCION = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/** ¿Con qué emoji reaccionó este operador a este mensaje? `undefined` si no reaccionó. */
export function miReaccion(
  reacciones: MessageReaction[] | undefined,
  miUserId: number | null | undefined,
): string | undefined {
  if (miUserId == null) return undefined;
  return (reacciones ?? []).find(
    (r) => r.reactorType === "operator" && r.reactorId === miUserId,
  )?.emoji;
}

/**
 * Qué emoji mandar al tocar uno.
 *
 * Tocar el que ya tenías **lo quita** — es el gesto de WhatsApp, y quitar se manda como emoji
 * vacío. Tocar otro lo reemplaza.
 */
export function emojiAEnviar(actual: string | undefined, tocado: string): string {
  return actual === tocado ? "" : tocado;
}
